import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowAltCircleDown } from "react-icons/fa";
import { getSettings } from "../../services/settingsServices";
import { toAbsolutePhotoUrl } from "../../services/settingsPhotoServices";
import Button from "../../components/Button/Button"; // adapte le chemin si besoin
import "./HeaderSlider.css";

// ✅ IMPORTANT : adapte le chemin si ton HeaderSlider n'est pas dans le même dossier que UserContext
import { useUser } from "./UserContext";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

const FALLBACK_1 = "/img/header_placeholder_1.jpg";
const FALLBACK_2 = "/img/header_placeholder_2.jpg";

const Header = ({ user: userProp }) => {
  const navigate = useNavigate();

  // ✅ Source fiable : contexte user (comme dans HomePageVitrine)
  const { user: userFromContext } = useUser();

  // ✅ Compat: si jamais tu passes encore user en props, on le garde en priorité
  const user = userProp ?? userFromContext;

  // ✅ Détection connecté
  const isLogged = useMemo(() => {
    if (!user) return false;
    // selon ta structure user (id, token, etc.), ajuste si besoin :
    if (typeof user === "object") return Boolean(user.id || user.email || user.token);
    return true;
  }, [user]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [slides, setSlides] = useState([]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getSettings();
        if (Array.isArray(settings) && settings.length > 0) {
          const s = settings[0];
          const headers = [];

          const src1 = toAbsolutePhotoUrl((s.header_url1 || "").trim());
          const src2 = toAbsolutePhotoUrl((s.header_url2 || "").trim());

          if (src1) {
            headers.push({
              src: src1,
              text: s.header_texte1 || "",
              align: "left",
              fallback: FALLBACK_1,
            });
          }
          if (src2) {
            headers.push({
              src: src2,
              text: s.header_texte2 || "",
              align: "right",
              fallback: FALLBACK_2,
            });
          }

          // ✅ Si aucune image en DB -> fallback visuel
          if (!headers.length) {
            headers.push({
              src: FALLBACK_1,
              text: s.header_texte1 || "",
              align: "left",
              fallback: FALLBACK_1,
            });
            headers.push({
              src: FALLBACK_2,
              text: s.header_texte2 || "",
              align: "right",
              fallback: FALLBACK_2,
            });
          }

          setSlides(headers);
          setCurrentIndex(0);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres:", error);
        // fallback minimal si l'API plante
        setSlides([
          { src: FALLBACK_1, text: "", align: "left", fallback: FALLBACK_1 },
          { src: FALLBACK_2, text: "", align: "right", fallback: FALLBACK_2 },
        ]);
        setCurrentIndex(0);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timeout = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 8000);

    return () => clearTimeout(timeout);
  }, [currentIndex, slides]);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;

    const navOffset = 90;
    const y = el.getBoundingClientRect().top + window.scrollY - navOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const handleScrollDown = () => scrollToId("cours");

  const handleInscription = () => {
    // ✅ Ici, on est censé être connecté (bouton visible uniquement si isLogged)
    navigate("/ajouter-inscription-adherent");
  };

  if (!slides.length) {
    return (
      <div className="header-hero-skeleton">
        <div className="header-skeleton-inner">Chargement des visuels...</div>
      </div>
    );
  }

  const activeSlide = slides[currentIndex];

  return (
    <header className="header-hero" data-align={activeSlide.align}>
      {/* Slides d'arrière-plan */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`header-slide ${index === currentIndex ? "active" : "inactive"}`}
          aria-hidden={index !== currentIndex}
        >
          {slide.src && (
            <img
              src={slide.src}
              alt={`Visuel ${index + 1}`}
              className="header-slide-image"
              loading={index === 0 ? "eager" : "lazy"}
              onError={(e) => {
                e.currentTarget.src =
                  slide.fallback || (index === 0 ? FALLBACK_1 : FALLBACK_2);
              }}
            />
          )}

          <div
            className={cn(
              "header-slide-overlay",
              slide.align === "right" ? "overlay-right" : "overlay-left"
            )}
          />
        </div>
      ))}

      {/* Contenu */}
      <div className="header-content">
        <div
          className={cn(
            "header-content-inner font-satoshi",
            activeSlide.align === "right" ? "header-content-right" : "header-content-left"
          )}
        >
          <div
            className="header-text"
            dangerouslySetInnerHTML={{ __html: activeSlide.text }}
          />

          {/* Chips navigation */}
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              ["Disciplines", "cours"],
              ["Équipes", "nosEquipes"],
              ["Groupes", "groupes"],
              ["Contact", "contact"],
            ].map(([label, id]) => (
              <button
                key={id}
                type="button"
                onClick={() => scrollToId(id)}
                className={cn(
                  "rounded-full px-3 py-2 text-xs font-bold",
                  "bg-white/10 text-slate-100",
                  "border border-slate-800/70",
                  "hover:border-primary/70 hover:text-primary transition"
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {/* ✅ CTA : selon connecté / non connecté */}
          <div className="header-cta-row">
            {!isLogged ? (
              <Button
                text="Créer un compte"
                variant="primary"
                size="lg"
                onClick={() => navigate("/register")}
                className="uppercase tracking-widest"
              />
            ) : (
              <Button
                text="Faire une inscription"
                variant="outline"
                size="lg"
                onClick={handleInscription}
                className="uppercase tracking-widest"
              />
            )}
          </div>
        </div>

        {/* Dots */}
        {slides.length > 1 && (
          <div className="header-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`header-dot ${i === currentIndex ? "active" : ""}`}
                onClick={() => setCurrentIndex(i)}
                aria-label={`Aller au visuel ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Flèche scroll */}
      <button
        type="button"
        onClick={handleScrollDown}
        className="header-scroll"
        aria-label="Descendre"
      >
        <FaArrowAltCircleDown size={40} />
      </button>
    </header>
  );
};

export default Header;